import React, { useState, useRef, useEffect, FormEvent } from "react";
import { getAllClassNames } from "helpers";
import {
  useRefinementList,
  UseRefinementListProps,
} from "react-instantsearch";
import styles from "./DropdownLocalAlgolia.module.scss";
import Text from "components/text/Text";
import { Arrow, Close, Search, /*Search*/ } from "assets/icons";
import strings from "assets/strings";
import { useClickOutside } from "hooks";
import Popup from "components/popup/Popup";
import Button from "components/buttons/button/Button";
import { grad1, grad2 } from "assets/constants";
import { SortBy } from "instantsearch.js";
import { SearchResults } from "algoliasearch-helper";
import { forEach } from "lodash";
import { ResetButton } from "components/buttons/resetButton/ResetButton";

//import { ResetButton } from "components/buttons/resetButton/ResetButton";
export type RefinementListProps = React.ComponentProps<"div"> &
  UseRefinementListProps & {
    title?: string;
    sortBy?: SortBy<SearchResults.FacetValue>;
    titlePopup?: string;
    subTitlePopUp?: string;
    multiSelect?: boolean;
    isPopup?: boolean;
    selectedBubbles?: string[];
    hide?: boolean;
    hideContinueBtn?: boolean;
  };

export const DropdownLocalAlgolia = ({
  attribute,
  title,
  sortBy,
  titlePopup,
  subTitlePopUp,
  multiSelect,
  isPopup,
  selectedBubbles,
  hide,
  hideContinueBtn,
  ...props
}: RefinementListProps) => {
  const { items, refine } = useRefinementList({ attribute, limit: 1000 });
  //const refinedItems = items.filter(item => item.isRefined);
  const [localItems, setLocalItems] = useState(items);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [filterText, setFilterText] = useState<string>(''); 
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [bubbles, setBubbles] = useState<Array<string>>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991.98);
  const popupRef = useRef<HTMLDivElement>(null);

  useClickOutside(dropdownRef, () => setShowDropdown(false));

  const filterItems = (str: string) => {
    if (str === '') setLocalItems(sortedItems);
    const tempItems = [...sortedItems];
    const localItems = tempItems.filter((item) => item.label.includes(str));
    setLocalItems(localItems);
    setFilterText(str);
  };
  const onReset = () => {
    items.forEach(item => {
      if (item.isRefined) {
        refine(item.value);
      }
    });
    setBubbles([]); // Reset bubbles
  };

  const handleToggleVisibility = () => {
    if (popupRef.current) {
      popupRef.current.classList.toggle('show');
    }
    //update showPopup state
    setShowPopup((prevState) => !prevState);
  };

  const isSelected = (value: any): boolean => {
    return bubbles.includes(value);
  }

  const handleResize = () => {
    setIsMobile(window.innerWidth < 992);
  };

  useEffect(() => {
    // Set up the event listener
    window.addEventListener('resize', handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const refinedItems = items.filter(item => item.isRefined);
    const newBubbles = refinedItems.length > 0
      ? refinedItems.map(item => item.label)
      : selectedBubbles || [];

    // Only update `bubbles` if it has changed
    if (JSON.stringify(newBubbles) !== JSON.stringify(bubbles)) {      
      setBubbles(newBubbles);
    }    
  }, [items, selectedBubbles]);

  useEffect(() => {
    //if(items.length === 0) return;
    setLocalItems(items.sort((a, b) => a.label.localeCompare(b.label)));
  }, [items]);

  useEffect(() => {
    if (!showPopup) return;
    const focusableElements = popupRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements?.[0] as HTMLElement;
    const lastElement = focusableElements?.[focusableElements.length - 1] as HTMLElement;

    const trapFocus = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };

    firstElement?.focus();
    document.addEventListener('keydown', trapFocus);

    return () => {
      document.removeEventListener('keydown', trapFocus);
    };
  }, [showPopup]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showDropdown) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDropdown]);

  useEffect(() => {
    if (focusedIndex !== -1 && showDropdown) {
      const focusedElement = document.querySelector(`.dropdown-item-${focusedIndex}`) as HTMLElement;
      if (focusedElement) {
        focusedElement.focus();
      }
    }
  }, [focusedIndex]);

  const handleCloseKeyDown = (event: any, item: string | undefined) => {
    // add mouse click event to the if statement
    if (event.key === "Enter" || event.key === " " || event.type === "click") {
      if (item) {
        event.preventDefault();
        refine(item);
      }
      const temp = [...bubbles];
      const index = temp.findIndex((bubble) => bubble === item);
      if (index !== -1) temp.splice(index, 1);
      else temp.push(item || '');
      setBubbles(temp);
    }
  };

  const handleReset = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setFilterText('');
  };

  const handleSelectItem = (value: string) => {
    const temp = [...bubbles];
    const index = temp.findIndex((bubble) => bubble === value);
    if (index !== -1) temp.splice(index, 1);
    else temp.push(value);
    setBubbles(temp);
    setBubbles(temp);
    refine(value);
    //items.sort((a, b) => a.label.localeCompare(b.label));
    setLocalItems(items.sort((a, b) => a.label.localeCompare(b.label)));
    setFilterText('');
  }

  const handleArrowKeyDown = (event: React.KeyboardEvent<SVGSVGElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setShowDropdown((prevState) => !prevState);
    }
  };

  const handleDropdownKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const maxIndex = sortedItems.length - 1;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setFocusedIndex((prevIndex) => Math.min(prevIndex + 1, maxIndex));
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setFocusedIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      refine(sortedItems[focusedIndex].label);
    }
  };


  const handleRadioChange = (value: string) => {
    forEach(items, (item) => {
      if (item.isRefined) {
        refine(item.value);
      }
    });
    refine(value);
  };

  const sortedItems = React.useMemo(() => {
    return [...items].sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  return items.length ? (
    <div className={getAllClassNames([styles.container, hide && styles.hide])} {...props}>
      <div className={styles.divTop}>
        {title && <Text className={styles.title} text={title} />}
        {isPopup && (
          <button
            tabIndex={0}
            onClick={() => handleToggleVisibility()}
            className={styles.listText}
          >
            {strings.doctorSearchResults.list}
          </button>
        )}
      </div>
      <div
        className={getAllClassNames([
          styles.dropdownContainer,
          !multiSelect && styles.paddingLeft,
        ])}
      >
        {sortedItems
          .filter((item) => item.isRefined)
          .map((item) => (
            <li
              key={item.value}
              className={getAllClassNames([
                styles.item,
                "ais-RefinementList-item",
                item.isRefined && "ais-RefinementList-item--selected",
              ])}
            >
              <span
                className={getAllClassNames([
                  "ais-RefinementList-labelText",
                  styles.label,
                ])}
              >
                {item.highlighted}
              </span>
              <Close
                onClick={(event) => handleCloseKeyDown(event, item.highlighted)}
                className={styles.icon}
                aria-label={`${strings.globalStrings.remove} ${item}`} // Added aria-label for accessibility
                role="button" // Added role for screen readers
                tabIndex={0} // Make the element focusable
                onKeyDown={(event) => handleCloseKeyDown(event, item.highlighted)}
              />
            </li>
          ))}
        {!multiSelect && (
          <Arrow
            onClick={() => setShowDropdown(true)}
            className={styles.iconArrow}
            aria-label={`${showDropdown ? strings.globalStrings.collapse : strings.globalStrings.expand} ${title}`} // Added aria-label for accessibility
            role="button" // Added role for screen readers
            tabIndex={0} // Make the element focusable
            onKeyDown={handleArrowKeyDown}
          />
        )}
      </div>
      {showDropdown && (
        <div ref={dropdownRef} className={styles.dropdown} onKeyDown={handleDropdownKeyDown}>
          {localItems.map((item, index) => (
            <div
              key={item.value}
              className={`dropdown-item-${index} ${getAllClassNames([
                "ais-RefinementList-item",
                item.isRefined && "ais-RefinementList-item--selected",
              ])}`}
              tabIndex={0} // Make the element focusable
            >
              <label>
                <input
                  type="checkbox"
                  value={item.value}
                  checked={(item.isRefined)}
                  className={styles.checkbox}
                  onChange={() => { handleSelectItem(item.value); }}
                />
                {item.label}
              </label>
            </div>
          ))}
          <div className={styles.height} />
        </div>
      )}
      {<Popup ref={popupRef} onClose={() => handleToggleVisibility()} style={isMobile ? {zIndex: '1001'} : {}}>
        <div className={getAllClassNames(["ais-SearchBox", styles.container])} ref={popupRef} style={isMobile ? { paddingInline: '20px' } : {}}>
          <h3 className={styles.titlePopup} id="modalTitle">{titlePopup}</h3>
          <Text className={styles.subTitle} text={subTitlePopUp} />
          {items.length > 7 && <form
            action=""
            className={getAllClassNames(["ais-SearchBox-form", styles.form])}
            noValidate
            // onSubmit={() => alert("submit")}
            onReset={handleReset}
          >
            <input
              aria-autocomplete="list" role="combobox" //aria-owns="divList"  aria-activedescendant="divList"
              className="ais-SearchBox-input"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              //placeholder={placeholder}
              spellCheck={false}
              maxLength={35}
              type="search"
              value={filterText}
              onChange={(e: any) => filterItems(e.target.value)}
            />
            {!filterText && <Search className={styles.searchIcon} />}
            <ResetButton hidden={filterText === undefined || filterText.length === 0} />
          </form>
          }
          <div className={styles.divWrapper}>
          {bubbles.length > 0 && (
            <div className={styles.divBubbles}>
              {bubbles.map((item) => (
                <div key={item} className={styles.bubble}>
                  <span className={styles.labelText}>{item}</span>
                  <Close
                    onClick={(event) => handleCloseKeyDown(event, item)}
                    className={styles.closeIcon}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => handleCloseKeyDown(event, item)}
                  />
                </div>
              ))}
            </div>
          )}
          <div className={styles.divList}>
            <ul className={styles.list}>
              {multiSelect && (localItems.length ? localItems : sortedItems)
                .map((item) => (
                  <li
                    key={item.value}
                    className={getAllClassNames([
                      "ais-RefinementList-item",
                      isSelected(item.value) && "ais-RefinementList-item--selected",
                    ])}
                  >
                    <label
                      key={item.label}
                      className={getAllClassNames([
                        "ais-RefinementList-label",
                        styles.labelPopup,
                      ])}
                    >
                      <input
                        className={getAllClassNames([
                          "ais-RefinementList-checkbox",
                          styles.checkbox,
                        ])}
                        type="checkbox"
                        value={item.value}
                        checked={isSelected(item.value)}
                        onChange={() => handleSelectItem(item.value)}
                      />
                      <span
                        className={getAllClassNames([
                          "ais-RefinementList-labelText",
                          styles.labelText,
                        ])}
                      >
                        {item.label}
                      </span>
                    </label>
                  </li>
                ))}
              {!multiSelect && sortedItems
                .map((item) => (
                  <li
                    key={item.value}
                    className={getAllClassNames([
                      "ais-RefinementList-item",
                      item.isRefined && "ais-RefinementList-item--selected",
                    ])}
                  >
                    <label
                      key={item.value}
                      className={getAllClassNames([
                        "ais-RefinementList-label",
                        item.isRefined && "ais-RefinementList-item--selected",
                        styles.labelPopup,
                      ])}
                    >
                      <input
                        className={getAllClassNames([
                          "ais-RefinementList-radio",
                          styles.radio,
                        ])}
                        type="radio"
                        value={item.value}
                        checked={item.isRefined}
                        onChange={() => handleRadioChange(item.value)}
                      />
                      <span
                        className={getAllClassNames([
                          "ais-RefinementList-labelText",
                          styles.labelText,
                        ])}
                      >
                        {item.label}
                      </span>
                    </label>
                  </li>
                ))}

            </ul>
          </div>
          </div>
          <div className={styles.viewBottom}>

            {multiSelect && <Button
              onClick={onReset}
              replaceClassName={styles.buttonClear}
              text={strings.globalStrings.clear}
            />}

            {!hideContinueBtn && <Button
              onClick={() => handleToggleVisibility()}
              color1={grad1}
              color2={grad2}
              replaceClassName={styles.buttonContinue}
              text={strings.globalStrings.continue}
            />
            }
          </div>
        </div>
      </Popup>
      }
    </div>
  ) : null;
};
